
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Opportunity } from "@/types/database-extensions";

interface FetchOpportunitiesProps {
  selectedType: string | null;
  searchQuery: string;
}

export const useFetchOpportunities = ({ selectedType, searchQuery }: FetchOpportunitiesProps) => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Get unique opportunity types
  const opportunityTypes = Array.from(
    new Set(opportunities.map(opportunity => opportunity.type).filter(Boolean))
  );

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      
      let queryBuilder = supabase.from("opportunities").select("*");
      
      if (selectedType) {
        queryBuilder = queryBuilder.eq("type", selectedType);
      }
      
      const { data, error } = await queryBuilder.order("created_at", { ascending: false });
      
      if (error) {
        console.log("Error fetching opportunities:", error.message);
        // Don't show error toast, just set empty array
        setOpportunities([]);
      } else {
        setOpportunities(data || []);
      }
    } catch (error: any) {
      console.log("Error fetching opportunities:", error.message);
      // Don't show error toast, just set empty array
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [selectedType]);

  // Filter opportunities based on search query
  const filteredOpportunities = opportunities.filter(opportunity => 
    opportunity.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    opportunity.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opportunity.organization.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return {
    opportunities: filteredOpportunities,
    loading,
    opportunityTypes,
    refreshOpportunities: fetchOpportunities,
  };
};
